import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PortalShell, StatusBadge } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import { useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";
import {
  fetchB2BRecentGeneratedImages,
  resolveB2BGeneratedImages,
  type B2BGeneratedImage,
} from "@/lib/b2bGenerationApi";

export const Route = createFileRoute("/business/portal/artwork")({
  component: ArtworkGalleryPage,
});

function ArtworkGalleryPage() {
  const { data: portalData, loading: portalLoading, error: portalError } = useBusinessPortalSnapshot();
  const tenantScopeKey = portalData.account?.id ?? "";
  const [images, setImages] = useState<B2BGeneratedImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setImages([]);
    setError("");
    setIsLoading(true);

    if (!tenantScopeKey) {
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    fetchB2BRecentGeneratedImages(72)
      .then((response) => {
        if (!cancelled) setImages(resolveB2BGeneratedImages(response));
      })
      .catch((nextError) => {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : "Unable to load generated artwork");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

      return () => {
        cancelled = true;
      };
  }, [tenantScopeKey]);

  return (
    <PortalShell
      active="/business/portal/artwork"
      eyebrow="Artwork"
      title="Artwork gallery"
      description="Generated concepts for your packaging orders."
      account={portalData.account}
      actions={(
        <Link to="/business/portal/brands" className="b2b-primary-button">
          Create artwork
        </Link>
      )}
    >
      <SEOHead title="Generated Artwork Gallery" description="Review generated B2B artwork concepts for Quuil packaging projects." path="/business/portal/artwork" robots="noindex,follow" />

      <section className="b2b-card b2b-gallery-intro-card">
        <div>
          <span className="b2b-kicker">Concepts</span>
          <h2>Recent artwork</h2>
        </div>
        <StatusBadge status={isLoading ? "loading" : `${images.length} ready`} tone={images.length > 0 ? "good" : "neutral"} />
      </section>

      {portalError && !portalLoading ? (
        <section className="b2b-card b2b-empty-state">
          <h2>Workspace unavailable</h2>
          <p>{portalError}</p>
        </section>
      ) : null}

      {error ? (
        <section className="b2b-card b2b-empty-state">
          <h2>Artwork could not be loaded</h2>
          <p>{error}</p>
        </section>
      ) : null}

      {isLoading ? (
        <section className="b2b-artwork-gallery-grid" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="b2b-generated-image-skeleton" />
          ))}
        </section>
      ) : images.length > 0 ? (
        <section className="b2b-artwork-gallery-grid" aria-label="Generated artwork">
          {images.map((image, index) => (
            <article key={image.id} className="b2b-card b2b-artwork-gallery-card">
              <img src={image.imageUrl} alt={`Generated artwork ${index + 1}`} loading="lazy" />
              <div>
                <span className="b2b-kicker">Concept {index + 1}</span>
                <h3>Generated artwork</h3>
                <p>Ready for review.</p>
                <Link to="/business/portal/projects/new" className="b2b-secondary-button">Use in order</Link>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="b2b-card b2b-empty-state">
          <h2>No generated artwork yet</h2>
          <p>Create artwork from Logos & Artwork, then the completed concepts will appear here.</p>
          <Link to="/business/portal/brands" className="b2b-primary-button">Start generation</Link>
        </section>
      )}
    </PortalShell>
  );
}
